import styled from '@emotion/styled';
import { Form, Row, Col, Space, Button, message } from 'antd';
import RobotIcon from 'components/RobotIcon';
import dayjs, { Dayjs } from 'lib/dayjs';
import { useEffect, useRef, useState, useMemo } from 'react';
import posthog from '@leyan/lytics';
import useRoles from 'hooks/business/useRoles';
import { putTalkAiReplyConf } from 'services/api/conversation.ai.reply.setting';
import { getReceptionPriority } from 'services/api/im.setting';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import {
  useConversationOfId,
  useUpdateConversationOfId,
} from 'hooks/business/conversationsHooks/useConversationOfId';
import { AiReceptionPopoverForm } from './AiReceptionForm';
import useCrud from 'hooks/useCrud';
import { useMount } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { useOrgConfigQAEnable } from 'hooks/business/useOrgConfig';

export const Wrapper = styled.div<{ mode: 'timer' | 'normal' }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 32px;
  height: 28px;
  padding: 0 5px;
  border-radius: 4px;
  box-sizing: border-box;
  justify-content: space-around;
  overflow: hidden;
  cursor: pointer;

  transition-property: width, background-color;
  transition-delay: 0, 1500ms;
  transition-duration: 300ms, 1000ms;
  transition-timing-function: ease-in-out, linear;
  ${({ mode }) => {
    if (mode === 'timer') {
      return {
        backgroundColor: '#f7f9fa',
        willChange: 'auto',
        width: '106px',
        transitionDelay: '0, 0ms',
        transitionDuration: '500ms, 100ms',
      };
    }
    return {};
  }}
`;
export const Timer = styled.span`
  font-size: 12px;
  color: #f2a100;
  margin-left: 8px;
`;
export const StyledRobotIcon = styled(RobotIcon)`
  background: none;
  font-size: 21px;
`;

export interface AiReceptionTimerProps {
  conversationId: Conversation.Conversation['conversationId'];
}

const defaultMoment = dayjs();
const AiReceptionTimer = (props: AiReceptionTimerProps) => {
  const { conversationId } = props;

  const { t } = useTranslate();

  const [popVisible, setPopVisible] = useState(false);
  const conversation = useConversationOfId(conversationId);

  const { isSuperviser } = useRoles();
  const [timer, setTimer] = useState('');
  const endTimeRef = useRef<Dayjs>(defaultMoment);
  const timeoutRef = useRef<NodeJS.Timeout>(-1 as any);
  const updateThisConv = useUpdateConversationOfId(conversationId);
  const [canSubmit, setCanSubmit] = useState(false);
  const endTime = parseInt(conversation?.pauseAiReplyEndMillis || '0', 10);
  const hasControl = conversation?.controlStatus;

  const { item: receptionSetting, fetch } = useCrud({
    read: getReceptionPriority,
  });

  useMount(() => {
    if (!conversation?.storeId) return;
    fetch({ storeId: conversation.storeId }).catch((e) => {
      errorToasterWithServerMsg(e, t('phrase.获取数据失败，请稍后再试'));
    });
  });

  const isManualPaused = conversation?.pauseAiReply || false;

  const {
    humanFirst = false,
    pauseAi: manualPauseAiEnabled = false,
    pauseMinute: defaultMinute = undefined,
  } = receptionSetting || {};

  useEffect(() => {
    if (!isManualPaused) {
      clearInterval(timeoutRef.current);
      setTimer('');
    } else if (endTime) {
      endTimeRef.current = dayjs(endTime);
      const countdown = () => {
        const now = dayjs();
        const { current: endTime } = endTimeRef;
        const diff = endTime.diff(now);
        if (diff <= 0) {
          clearInterval(timeoutRef.current);
          setTimer('');
          return;
        }
        const timer = dayjs.duration(diff).format('hh:mm:ss');
        setTimer(timer);
      };
      countdown();
      timeoutRef.current = setInterval(countdown, 1000);
    }
    return () => {
      clearInterval(timeoutRef.current);
    };
  }, [endTime, isManualPaused, conversationId, updateThisConv]);
  const [form] = Form.useForm();
  return useMemo(() => {
    const content = (
      <Wrapper
        mode={timer ? 'timer' : 'normal'}
        key="AiReceptionTimer"
        onClick={() => setPopVisible(true)}
      >
        {timer ? (
          <>
            <StyledRobotIcon color="#84868C" status="paused" />
            <Timer>{timer}</Timer>
          </>
        ) : (
          <>
            <StyledRobotIcon color="#84868C" status={humanFirst ? 'paused' : 'working'} />
          </>
        )}
      </Wrapper>
    );
    if (!manualPauseAiEnabled) {
      return content;
    }
    return (
      <AiReceptionPopoverForm
        isPaused={isManualPaused || humanFirst}
        destroyTooltipOnHide
        formProps={{
          endTimeStr: timer,
          form,
          initialValues: { paused: isManualPaused, minutes: defaultMinute || 30 },
          onValuesChange: () => setCanSubmit(true),
          onFinish: (values) => {
            const { paused: enablePause, minutes } = values;
            return putTalkAiReplyConf({
              conversationId,
              enable: enablePause,
              minutes,
              reason: 'MARK',
            })
              .then((rsp) => {
                posthog.capture('$AiReplyPause', {
                  info: {
                    ...values,
                    buyer: conversation?.buyerNick,
                    buyerId: conversation?.buyerId,
                  },
                });
                updateThisConv({
                  pauseAiReply: rsp.data.data.pauseAiReply,
                  pauseAiReplyEndMillis: rsp.data.data.pauseAiReplyEndMillis,
                });
                form.resetFields();
                form.setFieldsValue({ paused: enablePause, minutes });
                setCanSubmit(false);
                message.success(t('translation.设置成功'));
                setPopVisible(false);
              })
              .catch(errorToasterWithServerMsg);
          },
        }}
        // 没有控制权/会话设置没有打开“人工标记”功能时不能操作该表单, 超管拥有与当前接待人同等权限
        visible={((hasControl && manualPauseAiEnabled) || isSuperviser) && popVisible}
        content={
          <Row justify="end">
            <Col>
              <Space>
                <Button
                  onClick={(e) => {
                    form.resetFields();
                    e.nativeEvent.stopPropagation();
                    e.stopPropagation();
                    setPopVisible(false);
                  }}
                  size="small"
                >
                  {t('translation.取消')}
                </Button>
                <Button
                  disabled={!canSubmit}
                  onClick={() => {
                    form.submit();
                  }}
                  size="small"
                  type="primary"
                >
                  {t('translation.确定')}
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        {content}
      </AiReceptionPopoverForm>
    );
  }, [
    timer,
    humanFirst,
    manualPauseAiEnabled,
    isManualPaused,
    form,
    defaultMinute,
    hasControl,
    isSuperviser,
    popVisible,
    canSubmit,
    conversationId,
    conversation?.buyerNick,
    conversation?.buyerId,
    updateThisConv,
    t,
  ]);
};
// 没有QA权限时直接跳过渲染，可以减少不必要的渲染引发的fetch
const AiReceptionTimerWrap = (props: AiReceptionTimerProps) => {
  const qaEnable = useOrgConfigQAEnable();

  if (qaEnable) {
    return <AiReceptionTimer {...props} />;
  }
  return null;
};

export default AiReceptionTimerWrap;
